
import {
  getAllSections,
  getSectionsByType,
  insertSectionAfterNode,
  SectionCardData,
  temporarilyHideSectionOnCanvas,
  unhideTemporarilyHiddenSection
} from "@/lib/singularity/section-add-or-swap";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import SectionCard from "./SectionCard.vue";
import { TreeNode } from "@/types/tree";
import { documentStore, singularityStore, uiStore } from "@/store/store";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import IconButton from "@/components/UI/IconButton.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import {
  addFriendlyNameToSection,
  addPromptInfoToSection,
  getSectionTypes
} from "@/lib/singularity/singularity";
import { findNode } from "@/lib/tree/tree";
import { getPromptInfoFromNode } from "@/lib/singularity/util";
import { rewriteContentInSection } from "@/lib/singularity/sections";
import { aiJobController } from "@/types/singularity/ai-jobs";
import { replaceImagesInSectionWithAi } from "@/futurelayer/images/main";
import SingularityPanelHeader from "@/components/Singularity/PanelHeader/SingularityPanelHeader.vue";

@Component({
  components: {
    SectionCard,
    Dropdown,
    IconButton,
    DropdownInput,
    SingularityPanelHeader
  }
})
export default class SectionAddOrSwapPanel extends Vue {
  get sectionToSwapData() {
    if (!this.sectionAddOrSwapPanelState.sectionIdToAddAfterOrSwap) return null;
    const sectionToSwap = findNode(
      documentStore.document.tree,
      this.sectionAddOrSwapPanelState.sectionIdToAddAfterOrSwap
    );
    if (!sectionToSwap) return null;

    return getPromptInfoFromNode(sectionToSwap).singularity.sectionData;
  }

  currentSectionIsSame(index: number, type: string) {
    return (
      this.sectionToSwapData?.type === type &&
      this.sectionToSwapData?.index === index
    );
  }

  sectionType = this.sectionToSwapData?.type ?? "";

  updateSectionType(sectionType: string) {
    this.sectionType = sectionType;
  }

  get sectionAddOrSwapPanelState() {
    return singularityStore.sectionAddOrSwapPanelState;
  }

  get insertAfter(): TreeNode["id"] {
    return this.sectionAddOrSwapPanelState.sectionIdToAddAfterOrSwap ?? -1;
  }

  set insertAfter(value: TreeNode["id"]) {
    singularityStore.setSectionAddOrSwapPanelState({
      ...this.sectionAddOrSwapPanelState,
      sectionIdToAddAfterOrSwap: value
    });
  }

  get mode() {
    return this.sectionAddOrSwapPanelState.mode;
  }

  lastPreviewedSectionId: TreeNode["id"] | null = null;

  handleMouseEnter(section: SectionCardData, index: number) {
    if (this.mode === "swap") {
      temporarilyHideSectionOnCanvas(this.insertAfter);
    }

    const sectionDescription = {
      type: section.type,
      sectionFriendlyName: `${section.type} - ${index}`,
      description: ""
    };

    const sectionWithPrompt = addPromptInfoToSection(
      section.node,
      sectionDescription,
      index,
      false
    );

    const sectionWithFriendlyName = addFriendlyNameToSection(
      sectionWithPrompt,
      sectionDescription
    );

    this.lastPreviewedSectionId = insertSectionAfterNode(
      sectionWithFriendlyName,
      this.insertAfter
    );
  }

  handleMouseLeave() {
    if (this.lastPreviewedSectionId) {
      documentStore.deleteElement({ nodeId: this.lastPreviewedSectionId });
      this.lastPreviewedSectionId = null;
    }

    if (this.mode === "swap") {
      unhideTemporarilyHiddenSection();
    }
  }

  handleSectionChosen() {
    uiStore.activateElement(this.lastPreviewedSectionId);

    if (this.lastPreviewedSectionId) {
      const sectionId = this.lastPreviewedSectionId;
      const section = findNode(documentStore.document.tree, sectionId);
      if (!section) return;

      aiJobController.addJob(
        async abortController => {
          await rewriteContentInSection(sectionId, abortController);
          await replaceImagesInSectionWithAi(section, abortController);
        },
        `rewrite and image population for shouldGenerateOnInitialLoad for document
      "${documentStore.document.documentMeta.titleLabel} - ${documentStore.document.documentMeta.id}",
      section ${sectionId}`,
        sectionId
      );
    }

    this.lastPreviewedSectionId = null;

    if (this.mode === "swap") {
      documentStore.deleteElement({
        nodeId: this.insertAfter
      });
    }

    singularityStore.setSingularityLeftPanel("page_structure");
  }

  toggleMode() {
    singularityStore.setSectionAddOrSwapPanelState({
      ...this.sectionAddOrSwapPanelState,
      mode: this.mode === "add" ? "swap" : "add"
    });
  }

  get sections() {
    if (this.sectionType) {
      return getSectionsByType(this.sectionType);
    }
    return getAllSections();
  }

  get sectionTypes() {
    return getSectionTypes().sort((a, b) => {
      return a.localeCompare(b);
    });
  }
}
